import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [demoLink, setDemoLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.reset_url_demo) setDemoLink(data.reset_url_demo);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="forgot-page" className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-primary/60 hover:text-primary mb-8"><ArrowLeft size={16} /> {t("Back to sign in", "Retour à la connexion")}</Link>
        {sent ? (
          <div data-testid="forgot-sent">
            <span className="w-12 h-12 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center mb-5"><MailCheck size={22} /></span>
            <h1 className="font-display text-4xl font-medium text-primary">{t("Check your inbox", "Vérifiez vos courriels")}</h1>
            <p className="text-foreground/70 mt-3">{t("If that email is registered, we've sent a link to reset your password. It expires in 1 hour.", "Si ce courriel est enregistré, nous avons envoyé un lien pour réinitialiser votre mot de passe. Il expire dans 1 heure.")}</p>
            {demoLink && (
              <div className="mt-6 rounded-2xl border border-secondary/30 bg-secondary/5 p-5">
                <p className="text-sm text-foreground/70 mb-3">{t("Demo mode (email sending not configured). Use this link:", "Mode démo (envoi d'e-mails non configuré). Utilisez ce lien :")}</p>
                <a href={demoLink} data-testid="forgot-demo-link" className="text-secondary font-semibold break-all hover:underline">{demoLink}</a>
              </div>
            )}
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl md:text-5xl font-medium text-primary">{t("Forgot your password?", "Mot de passe oublié ?")}</h1>
            <p className="text-foreground/70 mt-3">{t("Enter your email and we'll send you a link to reset it.", "Entrez votre courriel et nous vous enverrons un lien de réinitialisation.")}</p>
            <form onSubmit={submit} className="space-y-5 mt-8" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">{t("Email", "Courriel")}</Label>
                <Input id="email" type="email" data-testid="forgot-email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl" />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
                {loading ? t("Sending...", "Envoi...") : t("Send reset link", "Envoyer le lien")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
